import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  findTerminologyUsages,
  mapTerminologyAdminError,
  parseTerminologyReferenceTokenCodes,
  resolveTerminologyText,
  TerminologyAdminDisplayError,
  TerminologyResolvedText,
  TerminologyUsageManifest,
  TerminologyUsageManifestRecord,
  TerminologyVocabularyCatalogItem,
  TerminologyVocabularyDefinitionDetail,
  TerminologyVocabularyPublicationDetail,
  TerminologyVocabularyRevisionHistoryItem,
  TerminologyVocabularyWordingUpdateRequest,
} from './platform-terminology-admin.models';
import {
  createTerminologyVocabularyMutationIntent,
  PlatformTerminologyAdminService,
  TerminologyVocabularyMutationIntent,
} from './platform-terminology-admin.service';
import { TerminologyService } from '@app/shared/services/terminology.service';
import { PlatformTerminologyHistoryHealthPanelComponent } from './platform-terminology-history-health-panel.component';

import {
  TerminologyEditField,
  TerminologyEditHistory,
  TerminologyEditSnapshot,
} from './terminology-edit-history';
import {
  getTerminologyReferenceOccurrences,
  planTerminologyReferenceEdit,
  TerminologyReferenceOccurrence,
} from './terminology-reference-editing';
import {
  compareTerminologyWording,
  TerminologyWordingDiff,
} from './terminology-wording-diff';

type PhraseInheritanceMode = 'composed' | 'literal';
export interface TerminologyEditorEditingState {
  dirty: boolean;
  busy: boolean;
}

export type TerminologyEditorWorkspaceView =
  | 'edit'
  | 'impact'
  | 'history'
  | 'technical';

interface EditorUsageSummary {
  usageId: string;
  componentCode: string;
  featureCode: string;
  surfaceCode: string;
  locationSummary: string;
  route: string | null;
  isSelected: boolean;
  isAuthoredTemplate: boolean;
  locations: { kind: string; value: string }[];
}

@Component({
  selector: 'phms-platform-terminology-vocabulary-editor',
  imports: [
    CommonModule,
    FormsModule,
    PlatformTerminologyHistoryHealthPanelComponent,
  ],
  templateUrl: './platform-terminology-vocabulary-editor.component.html',
  styleUrl: './platform-terminology-vocabulary-editor.component.scss',
})
export class PlatformTerminologyVocabularyEditorComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef, {
    optional: true,
  });
  private readonly localHistory = new TerminologyEditHistory();
  private activeField: TerminologyEditField = 'wording';
  private caretRequestId = 0;
  private composition: {
    field: TerminologyEditField;
    before: TerminologyEditSnapshot;
  } | null = null;
  private pendingInput: {
    before: TerminologyEditSnapshot;
    key?: string;
    composing: boolean;
  } | null = null;
  private readonly referenceSelection = signal<{
    content: string;
    start: number;
    end: number;
  } | null>(null);
  readonly historyMessage = signal<string | null>(null);
  private readonly service = inject(PlatformTerminologyAdminService);
  private readonly terminology = inject(TerminologyService);
  private historyRequestId = 0;
  private initializedIdentity = '';
  private initializedRevision = '';
  private operationGeneration = 0;
  private waitingForComparison = false;
  private helpTrigger: HTMLElement | null = null;
  private baselineContent = '';
  private baselineReason = '';
  private baselineInheritanceMode: PhraseInheritanceMode = 'composed';
  private pendingSaveIntent: TerminologyVocabularyMutationIntent | null = null;
  private serverValidationValue: string | null = null;
  private baselineReviewBehavior = 'Plain wording';
  private baselineReviewSource = 'Saved wording';
  private comparisonDiffCache: {
    current: string;
    proposal: string;
    value: TerminologyWordingDiff;
  } | null = null;
  private discardConfirmationIdentity = '';
  readonly confirmingUseCurrent = signal(false);

  @Input({ required: true }) detail!: TerminologyVocabularyDefinitionDetail;
  @Input() canAuthor = false;
  @Input() detailLoading = false;
  @Input() editorContextKey = '';
  @Input() usageManifest: TerminologyUsageManifest | null = null;
  @Input() selectedUsageId: string | null = null;
  @Input() initialView: TerminologyEditorWorkspaceView = 'edit';
  @Output() readonly editingStateChange =
    new EventEmitter<TerminologyEditorEditingState>();
  @Output() readonly accessLost = new EventEmitter<void>();
  @Output() readonly definitionSelected = new EventEmitter<number>();
  @Output() readonly contextReloadRequested = new EventEmitter<number>();
  @Output() readonly wordingSaved =
    new EventEmitter<TerminologyVocabularyPublicationDetail>();

  @ViewChild('wordingInput') private wordingInput?: ElementRef<
    HTMLInputElement | HTMLTextAreaElement
  >;

  @ViewChild('noteInput') private noteInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('referenceInsertion')
  private referenceInsertion?: ElementRef<HTMLDetailsElement>;
  @ViewChild('comparisonHeading')
  private comparisonHeading?: ElementRef<HTMLElement>;
  @ViewChild('discardConfirmation')
  private discardConfirmation?: ElementRef<HTMLElement>;
  @ViewChild('useCurrentButton')
  private useCurrentButton?: ElementRef<HTMLButtonElement>;

  @ViewChild('helpPanel') private helpPanel?: ElementRef<HTMLElement>;
  @ViewChild('moreMenu') private moreMenu?: ElementRef<HTMLDetailsElement>;

  readonly isSaving = signal(false);
  readonly isReloading = signal(false);
  readonly comparingCurrent = signal(false);
  readonly comparisonLoadFailed = signal(false);
  readonly fieldErrors = signal<string[]>([]);
  helpOpen = false;
  readonly saveMessage = signal<string | null>(null);
  readonly editorError = signal<TerminologyAdminDisplayError | null>(null);
  readonly revisionHistory = signal<TerminologyVocabularyRevisionHistoryItem[]>(
    [],
  );
  readonly isHistoryLoading = signal(false);
  readonly historyError = signal<TerminologyAdminDisplayError | null>(null);

  draftContent = '';
  changeReason = '';
  referenceSearch = '';
  inheritanceMode: PhraseInheritanceMode = 'composed';
  workspaceView: TerminologyEditorWorkspaceView = 'edit';

  ngOnChanges(changes: SimpleChanges): void {
    if (this.detail && (changes['detail'] || changes['editorContextKey'])) {
      const identity = this.contextIdentity();
      const changed = identity !== this.initializedIdentity;
      const revision = this.revisionIdentity();
      if (changed) {
        this.operationGeneration += 1;
        this.initializedIdentity = identity;
        this.workspaceView = this.initialView;
        this.resetEditorFromDefinition(false);
        this.focusWording();
      } else if (
        this.comparingCurrent() ||
        this.waitingForComparison ||
        (this.hasUnsavedChanges && revision !== this.initializedRevision)
      ) {
        this.operationGeneration += 1;
        this.waitingForComparison = false;
        this.isReloading.set(false);
        this.isSaving.set(false);
        this.comparingCurrent.set(true);
        this.workspaceView = 'edit';
        this.comparisonLoadFailed.set(false);
        this.clearComparisonDecision();
        this.helpOpen = false;
        this.closeMore();
        this.clearLocalHistory();
        this.pendingSaveIntent = null;
        this.editorError.set(null);
        this.publishEditingState();
        this.focusComparison('heading');
      } else if (!this.hasUnsavedChanges) {
        this.resetEditorFromDefinition(
          true,
          revision === this.initializedRevision,
        );
      }
      if (this.workspaceView === 'history') this.refreshRevisionHistory();
    } else if (
      changes['initialView'] &&
      this.initialView !== this.workspaceView
    ) {
      this.selectWorkspace(this.initialView);
    }
    if (changes['detailLoading']) {
      if (this.detailLoading) this.clearComparisonDecision();
      this.publishEditingState();
    }
    if (changes['canAuthor'] && !this.canAuthor) {
      this.clearLocalHistory();
      this.clearComparisonDecision();
    }
    if (
      changes['canAuthor'] &&
      this.canAuthor &&
      this.detail &&
      !this.localHistory.current
    )
      this.resetLocalHistory();
    if (changes['canAuthor'] && !this.canAuthor && this.isSaving()) {
      this.operationGeneration += 1;
      this.isSaving.set(false);
      this.publishEditingState();
    }
  }

  get busy(): boolean {
    return this.isSaving() || this.isReloading() || this.detailLoading;
  }

  get editRestriction(): string | null {
    if (this.detailLoading) return 'Loading current wording…';
    if (!this.canAuthor)
      return 'You do not have permission to edit this wording.';
    if (this.definition.definitionStatusCode !== 'active')
      return 'This definition is inactive and cannot be edited here.';
    if (!this.definition.isCustomerEditable)
      return 'This wording is product governed and cannot be edited here.';
    if (!this.detail.constraints.isValid)
      return 'Registered constraints are invalid. Editing is unavailable.';
    if (
      ((!this.definition.activeRevisionId ||
        !this.definition.activePublicationId) &&
        !this.isInitialWording) ||
      !this.definition.configurationVersionId
    )
      return 'No active wording is available to replace in this pack.';
    return null;
  }

  get canEdit(): boolean {
    return this.editRestriction === null;
  }

  get comparisonOriginalText(): string {
    return this.baselineContent;
  }
  get comparisonOriginalBehavior(): string {
    return this.baselineReviewBehavior;
  }
  get comparisonOriginalSource(): string {
    return this.baselineReviewSource;
  }
  get comparisonCurrentBehavior(): string {
    return this.definition.currentReferenceTokenCodes.length > 0
      ? 'Linked wording'
      : this.definition.currentCompositionBaseRevisionId != null
        ? 'Fixed wording'
        : 'Plain wording';
  }
  get comparisonCurrentSource(): string {
    return this.isInitialWording
      ? 'Product default'
      : this.definition.activeRevisionId && this.definition.activePublicationId
        ? 'Saved wording'
        : 'No active publication';
  }
  get comparisonProposalBehavior(): string {
    return this.inheritanceMode === 'composed'
      ? 'Linked wording'
      : this.baselineReviewBehavior !== 'Plain wording' ||
          this.supportsInheritanceChoice ||
          this.definition.currentCompositionBaseRevisionId != null
        ? 'Fixed wording'
        : 'Plain wording';
  }
  get comparisonWordingDiff(): TerminologyWordingDiff {
    const current = this.definition.currentText,
      proposal = this.draftContent;
    if (
      !this.comparisonDiffCache ||
      this.comparisonDiffCache.current !== current ||
      this.comparisonDiffCache.proposal !== proposal
    )
      this.comparisonDiffCache = {
        current,
        proposal,
        value: compareTerminologyWording(current, proposal),
      };
    return this.comparisonDiffCache.value;
  }
  get canResolveComparison(): boolean {
    return (
      this.comparingCurrent() &&
      this.canAuthor &&
      !this.busy &&
      !this.comparisonLoadFailed()
    );
  }

  get formMembers(): TerminologyVocabularyCatalogItem[] {
    const members = this.detail.familyMembers.length
      ? this.detail.familyMembers
      : [this.definition];
    const order = ['concept', 'plural', 'inline', 'inline-plural', 'uppercase'];
    const rank = (member: TerminologyVocabularyCatalogItem) => {
      const value = order.indexOf(
        member.kindCode === 'concept'
          ? 'concept'
          : (member.grammaticalFormCode ?? ''),
      );
      return value < 0 ? order.length : value;
    };
    return [...members].sort((left, right) => rank(left) - rank(right));
  }

  formLabel(member: TerminologyVocabularyCatalogItem): string {
    const code = member.grammaticalFormCode?.trim().toLowerCase();
    if (member.kindCode === 'concept' || !code) return 'Name';
    return (
      (
        {
          plural: 'Plural',
          inline: 'In a sentence',
          'inline-plural': 'Plural in a sentence',
          uppercase: 'All capitals',
        } as Record<string, string>
      )[code] ?? this.toDisplayLabel(code)
    );
  }

  openHelp(event: Event): void {
    this.helpTrigger = event.currentTarget as HTMLElement;
    this.helpOpen = true;
    window.setTimeout(() => this.helpPanel?.nativeElement.focus());
  }

  closeHelp(): void {
    this.helpOpen = false;
    this.helpTrigger?.focus();
  }

  get definition(): TerminologyVocabularyCatalogItem {
    const definition = this.detail.definition;
    const initial = definition.initialWording;
    return initial &&
      !definition.activeRevisionId &&
      !definition.activePublicationId
      ? {
          ...definition,
          currentReferenceTokenCodes: parseTerminologyReferenceTokenCodes(
            definition.currentText,
          ),
          configurationVersionId: initial.configurationVersionId,
          versionCode: initial.versionCode,
          localeCode: initial.localeCode,
          channelCode: initial.channelCode,
          audienceCode: initial.audienceCode,
          tenantId: initial.tenantId,
        }
      : definition;
  }

  get isInitialWording(): boolean {
    return (
      !!this.detail.definition.initialWording &&
      !this.detail.definition.activeRevisionId &&
      !this.detail.definition.activePublicationId
    );
  }

  get previewHasUnresolvedReferences(): boolean {
    return (
      parseTerminologyReferenceTokenCodes(this.resolvedDraft.text).length > 0
    );
  }

  get fixedWordingRestriction(): string | null {
    if (this.previewHasUnresolvedReferences)
      return 'Unavailable while references remain unresolved.';
    if (
      this.isInitialWording &&
      this.detail.constraints.requiredReferenceTokenCodes.length
    )
      return 'Save the linked wording first before creating a fixed version.';
    return null;
  }

  get isPhrase(): boolean {
    const kind = this.definition.kindCode.trim().toLowerCase();
    return kind === 'phrase' || kind === 'message';
  }

  get supportsInheritanceChoice(): boolean {
    return this.isPhrase && this.compositionTemplate !== null;
  }

  get compositionTemplate(): string | null {
    if (this.definition.currentReferenceTokenCodes.length > 0) {
      return this.definition.currentText;
    }

    const pinnedTemplate = this.definition.currentCompositionBaseText?.trim();
    return pinnedTemplate &&
      parseTerminologyReferenceTokenCodes(pinnedTemplate).length > 0
      ? pinnedTemplate
      : null;
  }

  get maximumLength(): number {
    return this.detail.constraints.maximumLength ?? 2000;
  }

  get minimumLength(): number | null {
    return this.detail.constraints.minimumLength ?? null;
  }

  get resolvedPublished(): TerminologyResolvedText {
    return resolveTerminologyText(
      this.definition.currentText,
      this.detail.referenceOptions,
    );
  }

  get resolvedDraft(): TerminologyResolvedText {
    return resolveTerminologyText(
      this.draftContent,
      this.detail.referenceOptions,
    );
  }

  get allowedReferenceOptions(): TerminologyVocabularyCatalogItem[] {
    const allowed = this.detail.constraints.allowedReferenceTokenCodes;
    return this.detail.referenceOptions.filter(
      (option) =>
        allowed == null ||
        allowed.some(
          (tokenCode) =>
            tokenCode.toLowerCase() === option.tokenCode.toLowerCase(),
        ),
    );
  }

  get filteredReferenceOptions(): TerminologyVocabularyCatalogItem[] {
    const search = this.referenceSearch.trim().toLowerCase();
    return this.allowedReferenceOptions.filter((option) => {
      return (
        !search ||
        [
          option.currentText,
          option.tokenCode,
          option.familyCode,
          option.grammaticalFormCode,
        ].some((value) => value?.toLowerCase().includes(search))
      );
    });
  }

  get referenceEditPlan() {
    const selected = this.referenceSelection();
    const current = selected?.content === this.draftContent ? selected : null;
    return planTerminologyReferenceEdit(
      this.draftContent,
      current?.start ?? this.draftContent.length,
      current?.end ?? this.draftContent.length,
    );
  }

  get canChangeReferences(): boolean {
    return (
      this.canEdit &&
      !this.busy &&
      !this.comparingCurrent() &&
      this.composition === null
    );
  }

  get wordingReferences() {
    return getTerminologyReferenceOccurrences(this.draftContent).map(
      (reference, index) => {
        const option = this.detail.referenceOptions.find(
          (candidate) =>
            candidate.tokenCode.toLowerCase() === reference.tokenCode,
        );
        return {
          ...reference,
          ordinal: index + 1,
          resolvedText: option?.currentText ?? null,
          isKnown: !!option,
        };
      },
    );
  }

  trackReferenceOccurrence(
    _index: number,
    reference: TerminologyReferenceOccurrence,
  ): string {
    return `${reference.start}:${reference.end}:${reference.rawText}`;
  }

  get invalidReferenceOccurrences() {
    const allowed = this.detail.constraints.allowedReferenceTokenCodes;
    return this.wordingReferences.filter(
      (reference) =>
        !this.detail.constraints.allowReferences ||
        this.inheritanceMode === 'literal' ||
        !reference.isKnown ||
        (allowed != null &&
          !allowed.some(
            (token) => token.toLowerCase() === reference.tokenCode,
          )),
    );
  }

  get referenceAppendBlockedReason(): string | null {
    return planTerminologyReferenceEdit(
      this.draftContent,
      this.draftContent.length,
      this.draftContent.length,
    ).blockedReason;
  }

  get missingRequiredReferenceOptions(): TerminologyVocabularyCatalogItem[] {
    if (
      !this.detail.constraints.allowReferences ||
      !this.mustKeepRequiredReferences
    )
      return [];
    const present = parseTerminologyReferenceTokenCodes(this.draftContent);
    return this.allowedReferenceOptions.filter(
      (option) =>
        this.detail.constraints.requiredReferenceTokenCodes.some(
          (token) => token.toLowerCase() === option.tokenCode.toLowerCase(),
        ) && !present.includes(option.tokenCode.toLowerCase()),
    );
  }

  captureReferenceSelection(): void {
    const input = this.wordingInput?.nativeElement;
    if (!input || input.value !== this.draftContent) return;
    this.referenceSelection.set({
      content: this.draftContent,
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length,
    });
  }

  findReference(reference: TerminologyReferenceOccurrence): void {
    if (
      !this.canChangeReferences ||
      !getTerminologyReferenceOccurrences(this.draftContent).some(
        (candidate) =>
          candidate.start === reference.start &&
          candidate.end === reference.end &&
          candidate.rawText === reference.rawText,
      )
    )
      return;
    this.workspaceView = 'edit';
    this.referenceSearch = '';
    if (this.referenceInsertion)
      this.referenceInsertion.nativeElement.open = true;
    this.localHistory.breakGroup();
    this.restorePosition({
      ...this.editSnapshot('wording'),
      selectionStart: reference.start,
      selectionEnd: reference.end,
    });
    this.historyMessage.set(
      `Selected linked wording ${reference.rawText}. Choose a replacement or edit the selection.`,
    );
  }

  chooseInsertionAtEnd(): void {
    if (!this.canChangeReferences) return;
    const end = this.draftContent.length;
    this.localHistory.breakGroup();
    this.restorePosition({
      ...this.editSnapshot('wording'),
      selectionStart: end,
      selectionEnd: end,
    });
  }

  addRequiredReference(option: TerminologyVocabularyCatalogItem): void {
    if (
      !this.canChangeReferences ||
      this.referenceAppendBlockedReason ||
      !this.missingRequiredReferenceOptions.some(
        (candidate) =>
          candidate.definitionId === option.definitionId &&
          candidate.tokenCode === option.tokenCode,
      )
    )
      return;
    const end = this.draftContent.length;
    this.applyReference(
      option,
      end,
      end,
      this.draftContent && !/\s$/.test(this.draftContent) ? ' ' : '',
    );
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.draftContent !== this.baselineContent ||
      this.changeReason !== this.baselineReason ||
      this.inheritanceMode !== this.baselineInheritanceMode
    );
  }

  get usageCount(): number {
    return Math.max(this.definition.deployedUsageCount, this.usageRows.length);
  }

  get usageRows(): EditorUsageSummary[] {
    const records = new Map<string, TerminologyUsageManifestRecord>();
    findTerminologyUsages(
      this.usageManifest,
      this.definition.configurationCode,
      this.definition.tokenCode,
    ).forEach((record) => records.set(record.usageId, record));
    this.detail.usages.forEach((usage) =>
      records.set(usage.usageId, {
        ...usage,
        configurationCode: this.definition.configurationCode,
        localeCode: this.definition.localeCode,
        tokenCode: this.definition.tokenCode,
        definitionKind: this.definition.kindCode,
        sourceSide:
          usage.sourceSide.toLowerCase() === 'authored-document'
            ? 'authored-document'
            : usage.sourceSide.toLowerCase() === 'server'
              ? 'server'
              : 'client',
      }),
    );

    return [...records.values()]
      .map((record) => {
        const locations = record.locations.map((location) => ({
          kind: location.kind,
          value: location.value,
        }));
        return {
          usageId: record.usageId,
          componentCode: record.componentCode,
          featureCode: record.featureCode,
          surfaceCode: record.surfaceCode,
          locationSummary:
            locations.map((location) => location.value).join(' · ') ||
            'Location unavailable',
          route: this.safeRoute(locations),
          isSelected: record.usageId === this.selectedUsageId,
          isAuthoredTemplate: record.sourceSide === 'authored-document',
          locations,
        };
      })
      .sort(
        (left, right) =>
          Number(right.isSelected) - Number(left.isSelected) ||
          left.featureCode.localeCompare(right.featureCode) ||
          left.usageId.localeCompare(right.usageId),
      );
  }

  get visibleUsageRows(): EditorUsageSummary[] {
    return this.usageRows.slice(0, 6);
  }

  trackUsage(_index: number, usage: EditorUsageSummary): string {
    return usage.usageId;
  }

  get characterCount(): number {
    return [...this.draftContent].length;
  }

  get shouldShowCharacterCount(): boolean {
    return this.characterCount >= Math.floor(this.maximumLength * 0.8);
  }

  selectInheritanceMode(mode: PhraseInheritanceMode): void {
    if (
      !this.canEdit ||
      this.busy ||
      this.comparingCurrent() ||
      this.composition !== null ||
      mode === this.inheritanceMode
    ) {
      return;
    }

    if (mode === 'literal' && this.fixedWordingRestriction) return;
    const before = this.editSnapshot('wording');
    if (mode === 'literal') {
      this.draftContent = this.resolvedDraft.text;
    } else if (
      parseTerminologyReferenceTokenCodes(this.draftContent).length === 0 &&
      this.compositionTemplate !== null
    ) {
      this.draftContent = this.compositionTemplate;
    }
    this.inheritanceMode = mode;
    this.recordLocalEdit('wording', before);
    this.restorePosition(this.editSnapshot('wording'));
  }

  selectWorkspace(view: TerminologyEditorWorkspaceView): void {
    if (this.comparingCurrent()) return;
    this.workspaceView = view;
    this.closeMore();
    if (view === 'edit') this.focusWording();
    if (view === 'history') {
      this.refreshRevisionHistory();
    }
  }

  closeMore(event?: Event): void {
    const menu = this.moreMenu?.nativeElement;
    if (!menu?.open) return;
    event?.preventDefault();
    menu.open = false;
    menu.querySelector('summary')?.focus();
  }

  @HostListener('document:pointerdown', ['$event'])
  dismissMoreOutside(event: PointerEvent): void {
    const menu = this.moreMenu?.nativeElement;
    if (
      menu?.open &&
      event.target instanceof Node &&
      !menu.contains(event.target)
    )
      menu.open = false;
  }

  selectDefinition(definitionId: number): void {
    if (this.busy || definitionId === this.definition.definitionId) {
      return;
    }
    if (
      this.hasUnsavedChanges &&
      typeof window !== 'undefined' &&
      !window.confirm('Discard unsaved wording changes and open another form?')
    ) {
      return;
    }
    this.definitionSelected.emit(definitionId);
  }

  insertReference(option: TerminologyVocabularyCatalogItem): void {
    if (
      !this.canEdit ||
      this.busy ||
      this.comparingCurrent() ||
      this.composition !== null ||
      !this.detail.constraints.allowReferences ||
      !this.filteredReferenceOptions.some(
        (candidate) =>
          candidate.definitionId === option.definitionId &&
          candidate.tokenCode === option.tokenCode,
      )
    )
      return;
    this.captureReferenceSelection();
    const plan = this.referenceEditPlan;
    if (plan.blockedReason) return;
    this.applyReference(option, plan.start, plan.end);
  }

  private applyReference(
    option: TerminologyVocabularyCatalogItem,
    start: number,
    end: number,
    separator = '',
  ): void {
    const before = this.editSnapshot('wording');
    const reference = `${separator}{{${option.tokenCode}}}`;
    this.draftContent = `${this.draftContent.slice(0, start)}${reference}${this.draftContent.slice(end)}`;
    this.inheritanceMode = 'composed';
    const after = {
      ...this.editSnapshot('wording'),
      selectionStart: start + reference.length,
      selectionEnd: start + reference.length,
    };
    const changed = this.localHistory.record(after, {
      kind: 'atomic',
      timestamp: performance.now(),
      before,
    });
    if (changed) this.markEditorChanged();
    this.restorePosition(after);
    this.historyMessage.set(
      changed
        ? start === end
          ? 'Inserted linked wording.'
          : 'Replaced the selected wording.'
        : 'Linked wording is unchanged.',
    );
  }

  saveWording(): void {
    if (this.busy || this.comparingCurrent() || this.composition !== null)
      return;
    if (!this.canEdit) {
      this.editorError.set({
        title: 'Editing unavailable',
        messages: [this.editRestriction!],
        issueCodes: [],
      });
      return;
    }
    const validationMessages = this.validateEditor();
    if (!this.hasCurrentServerValidation || validationMessages.length > 0)
      this.fieldErrors.set(validationMessages);
    if (validationMessages.length > 0) {
      this.workspaceView = 'edit';
      this.focusWording();
      return;
    }

    const definition = this.definition;
    if (
      ((!definition.activeRevisionId || !definition.activePublicationId) &&
        !this.isInitialWording) ||
      !definition.configurationVersionId
    ) {
      this.editorError.set({
        title: 'Active wording unavailable',
        messages: [
          'This entry needs an active terminology value before its wording can be changed here.',
        ],
        issueCodes: [],
      });
      return;
    }

    const generation = ++this.operationGeneration;
    const identity = this.contextIdentity();
    const request = this.createUpdateRequest();
    this.isSaving.set(true);
    this.publishEditingState();
    if (!this.hasCurrentServerValidation) this.editorError.set(null);
    this.saveMessage.set(null);
    this.pendingSaveIntent ??= createTerminologyVocabularyMutationIntent();
    this.service
      .updateVocabularyWording(
        definition.definitionId,
        request,
        this.pendingSaveIntent,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (publication) => {
          if (
            generation !== this.operationGeneration ||
            identity !== this.contextIdentity()
          )
            return;
          if (
            publication.definitionId !== definition.definitionId ||
            publication.configurationVersionId !==
              definition.configurationVersionId ||
            publication.tokenCode !== definition.tokenCode ||
            publication.contentText !== request.contentText ||
            publication.configurationCode !== definition.configurationCode ||
            publication.scope?.configurationCode !==
              definition.configurationCode ||
            publication.scope?.localeCode !== definition.localeCode ||
            (publication.scope?.channelCode ?? '') !== definition.channelCode ||
            (publication.scope?.audienceCode ?? '') !==
              definition.audienceCode ||
            (publication.scope?.tenantId ?? '') !== definition.tenantId
          ) {
            this.isSaving.set(false);
            this.editorError.set({
              title: 'Save could not be confirmed',
              messages: [
                'The response did not match this wording. Compare current wording before trying again.',
              ],
              issueCodes: [],
              statusCode: 409,
            });
            this.publishEditingState();
            return;
          }
          this.isSaving.set(false);
          this.adoptPublication(publication);
          this.draftContent = publication.contentText;
          this.changeReason = '';
          this.inheritanceMode =
            parseTerminologyReferenceTokenCodes(publication.contentText)
              .length > 0
              ? 'composed'
              : 'literal';
          this.captureBaseline();
          this.resetLocalHistory();
          this.pendingSaveIntent = null;
          this.serverValidationValue = null;
          this.editorError.set(null);
          this.fieldErrors.set([]);
          this.saveMessage.set('Wording saved to the active publication.');
          this.terminology.reload();
          this.publishEditingState();
          this.wordingSaved.emit(publication);
        },
        error: (error) => {
          if (
            generation !== this.operationGeneration ||
            identity !== this.contextIdentity()
          )
            return;
          this.isSaving.set(false);
          const mapped = mapTerminologyAdminError(error);
          this.serverValidationValue =
            mapped.statusCode === 400 || mapped.statusCode === 422
              ? this.validationValue()
              : null;
          if (mapped.statusCode === 401 || mapped.statusCode === 403) {
            this.operationGeneration += 1;
            this.clearLocalHistory();
            this.draftContent = '';
            this.changeReason = '';
            this.baselineContent = '';
            this.baselineReason = '';
            this.inheritanceMode = this.baselineInheritanceMode;
            this.pendingSaveIntent = null;
            this.comparingCurrent.set(false);
            this.fieldErrors.set([]);
            this.publishEditingState();
            this.accessLost.emit();
          }
          this.editorError.set(mapped);
          if (mapped.statusCode === 400 || mapped.statusCode === 422) {
            this.fieldErrors.set(mapped.messages);
            this.workspaceView = 'edit';
            this.focusWording();
          }
          this.publishEditingState();
        },
      });
  }

  resetChanges(): void {
    if (
      !this.canEdit ||
      this.busy ||
      this.comparingCurrent() ||
      this.composition !== null ||
      !this.hasUnsavedChanges
    )
      return;
    const before = this.editSnapshot();
    this.draftContent = this.baselineContent;
    this.changeReason = this.baselineReason;
    this.inheritanceMode = this.baselineInheritanceMode;
    this.recordLocalEdit(before.field, before);
    this.editorError.set(null);
    this.fieldErrors.set([]);
    this.historyMessage.set('Changes reset. Undo can restore your edit.');
    this.restorePosition(this.editSnapshot(before.field));
  }

  reloadCurrentWording(): void {
    if (this.busy) return;
    this.clearComparisonDecision();
    this.waitingForComparison = true;
    this.comparisonLoadFailed.set(false);
    this.isReloading.set(true);
    this.editorError.set(null);
    this.saveMessage.set(null);
    this.publishEditingState();
    this.contextReloadRequested.emit(this.definition.definitionId);
  }

  failCurrentWordingReload(error: unknown): void {
    if (!this.waitingForComparison) return;
    this.waitingForComparison = false;
    this.isReloading.set(false);
    this.comparisonLoadFailed.set(true);
    this.editorError.set(mapTerminologyAdminError(error));
    this.publishEditingState();
  }

  requestUseCurrentWording(): void {
    if (!this.canResolveComparison) return;
    this.discardConfirmationIdentity = this.comparisonDecisionIdentity();
    this.confirmingUseCurrent.set(true);
    this.focusComparison('discard');
  }

  cancelUseCurrentWording(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.clearComparisonDecision();
    this.focusComparison('useCurrent');
  }

  useCurrentWording(): void {
    if (
      !this.canResolveComparison ||
      !this.confirmingUseCurrent() ||
      this.discardConfirmationIdentity !== this.comparisonDecisionIdentity()
    )
      return;
    this.resetEditorFromDefinition(false);
    this.workspaceView = 'edit';
    this.focusWording();
  }

  keepMyWording(): void {
    if (!this.canResolveComparison || !this.canEdit) return;
    this.clearComparisonDecision();
    this.baselineContent = this.definition.currentText;
    this.baselineReason = '';
    this.baselineInheritanceMode = this.definition.currentReferenceTokenCodes
      .length
      ? 'composed'
      : 'literal';
    this.captureReviewBaseline();
    this.initializedRevision = this.revisionIdentity();
    this.pendingSaveIntent = null;
    this.comparingCurrent.set(false);
    const proposal = this.editSnapshot();
    this.localHistory.reset({
      ...proposal,
      content: this.baselineContent,
      reason: this.baselineReason,
      mode: this.baselineInheritanceMode,
    });
    this.localHistory.record(proposal, {
      kind: 'atomic',
      timestamp: performance.now(),
    });
    this.fieldErrors.set(this.validateEditor());
    this.editorError.set(null);
    this.publishEditingState();
    this.workspaceView = 'edit';
    this.focusWording();
  }

  markEditorChanged(): void {
    ++this.caretRequestId;
    this.pendingInput = null;
    this.historyMessage.set(null);
    this.pendingSaveIntent = null;
    this.saveMessage.set(null);
    const retainServerValidation = this.hasCurrentServerValidation;
    if (
      [400, 422].includes(this.editorError()?.statusCode ?? 0) &&
      !retainServerValidation
    ) {
      this.editorError.set(null);
      this.serverValidationValue = null;
    }
    if (this.fieldErrors().length && !retainServerValidation)
      this.fieldErrors.set(this.validateEditor());
    this.referenceSelection.set(null);
    this.captureReferenceSelection();
    this.publishEditingState();
  }

  get canSaveWording(): boolean {
    return (
      this.canEdit &&
      !this.busy &&
      !this.comparingCurrent() &&
      this.composition === null &&
      (this.hasUnsavedChanges || this.isInitialWording)
    );
  }

  get canUndo(): boolean {
    return (
      this.canEdit &&
      !this.busy &&
      !this.comparingCurrent() &&
      this.composition === null &&
      this.localHistory.canUndo
    );
  }

  get canRedo(): boolean {
    return (
      this.canEdit &&
      !this.busy &&
      !this.comparingCurrent() &&
      this.composition === null &&
      this.localHistory.canRedo
    );
  }

  beginTextEdit(field: TerminologyEditField, event: InputEvent): void {
    if (!this.canEdit || this.busy || this.comparingCurrent()) return;
    if (
      event.inputType === 'historyUndo' ||
      event.inputType === 'historyRedo'
    ) {
      event.preventDefault();
      if (event.inputType === 'historyUndo') this.undoEdit();
      else this.redoEdit();
      return;
    }
    if (this.composition?.field === field) return;
    if (this.pendingInput?.composing && event.isComposing) return;
    const before = this.editSnapshot(field);
    const current = this.localHistory.current;
    if (
      current &&
      (current.field !== field ||
        current.selectionStart !== before.selectionStart ||
        current.selectionEnd !== before.selectionEnd)
    )
      this.localHistory.breakGroup();
    this.activeField = field;
    const typing =
      !event.isComposing &&
      ['insertText', 'deleteContentBackward', 'deleteContentForward'].includes(
        event.inputType,
      );
    this.pendingInput = {
      before,
      key: typing ? field + ':' + event.inputType : undefined,
      composing: event.isComposing,
    };
  }

  focusEditField(field: TerminologyEditField): void {
    this.activeField = field;
    this.localHistory.breakGroup();
    if (field === 'wording') this.captureReferenceSelection();
  }

  startComposition(field: TerminologyEditField): void {
    if (!this.canEdit || this.busy || this.comparingCurrent()) return;
    this.composition = { field, before: this.editSnapshot(field) };
    this.localHistory.breakGroup();
  }

  finishComposition(field: TerminologyEditField): void {
    const composition = this.composition,
      generation = this.operationGeneration,
      identity = this.contextIdentity();
    if (!composition || composition.field !== field) return;
    // Angular's value accessor may commit its buffered value later in this same DOM event.
    void Promise.resolve().then(() => {
      if (
        this.destroyRef.destroyed ||
        this.composition !== composition ||
        generation !== this.operationGeneration ||
        identity !== this.contextIdentity()
      )
        return;
      this.composition = null;
      if (this.canEdit && !this.busy && !this.comparingCurrent())
        this.recordLocalEdit(field, composition.before);
    });
  }

  endTypingGroup(): void {
    this.localHistory.breakGroup();
  }

  onTextChanged(field: TerminologyEditField): void {
    if (!this.canEdit || this.busy || this.comparingCurrent()) return;
    if (this.composition?.field === field) {
      this.markEditorChanged();
      return;
    }
    const pending = this.pendingInput;
    const key = pending?.before.field === field ? pending.key : undefined;
    this.activeField = field;
    this.localHistory.record(this.editSnapshot(field), {
      kind: key ? 'typing' : 'atomic',
      timestamp: performance.now(),
      coalesceKey: key,
      before: pending?.before.field === field ? pending.before : undefined,
    });
    this.markEditorChanged();
  }

  undoEdit(): void {
    if (!this.canUndo) return;
    this.localHistory.record(this.editSnapshot(), {
      kind: 'atomic',
      timestamp: performance.now(),
    });
    const snapshot = this.localHistory.undo();
    if (snapshot) this.applyLocalSnapshot(snapshot, 'Undid the last edit.');
  }

  redoEdit(): void {
    if (!this.canRedo) return;
    this.localHistory.record(this.editSnapshot(), {
      kind: 'atomic',
      timestamp: performance.now(),
    });
    const snapshot = this.localHistory.redo();
    if (snapshot) this.applyLocalSnapshot(snapshot, 'Redid the last edit.');
  }

  @HostListener('keydown', ['$event'])
  handleEditorShortcut(event: KeyboardEvent): void {
    if (
      !(event.ctrlKey || event.metaKey) ||
      event.altKey ||
      event.isComposing ||
      this.composition !== null ||
      event.keyCode === 229 ||
      this.workspaceView !== 'edit' ||
      this.helpOpen ||
      !(event.target instanceof HTMLElement) ||
      !this.host?.nativeElement.contains(event.target)
    )
      return;
    const key = event.key.toLowerCase();
    const save = key === 's' && !event.shiftKey;
    const undo = key === 'z' && !event.shiftKey;
    const redo =
      (key === 'z' && event.shiftKey) || (key === 'y' && !event.shiftKey);
    if (!save && !undo && !redo) return;
    if (
      !save &&
      event.target.matches('input,textarea,[contenteditable="true"]') &&
      event.target !== this.wordingInput?.nativeElement &&
      event.target !== this.noteInput?.nativeElement
    )
      return;
    event.preventDefault();
    if (event.repeat) return;
    if (save) {
      if (this.canSaveWording) this.saveWording();
    } else if (undo) this.undoEdit();
    else this.redoEdit();
  }

  private editSnapshot(field = this.activeField): TerminologyEditSnapshot {
    const element =
      field === 'note'
        ? this.noteInput?.nativeElement
        : this.wordingInput?.nativeElement;
    const length = (field === 'note' ? this.changeReason : this.draftContent)
      .length;
    return {
      content: this.draftContent,
      reason: this.changeReason,
      mode: this.inheritanceMode,
      field,
      selectionStart: element
        ? Math.min(element.selectionStart ?? length, length)
        : null,
      selectionEnd: element
        ? Math.min(element.selectionEnd ?? length, length)
        : null,
      scrollTop: element?.scrollTop ?? 0,
      scrollLeft: element?.scrollLeft ?? 0,
    };
  }

  private recordLocalEdit(
    field: TerminologyEditField,
    before: TerminologyEditSnapshot,
  ): void {
    this.activeField = field;
    this.localHistory.record(this.editSnapshot(field), {
      kind: 'atomic',
      timestamp: performance.now(),
      before,
    });
    this.markEditorChanged();
  }

  private applyLocalSnapshot(
    snapshot: TerminologyEditSnapshot,
    message: string,
  ): void {
    this.draftContent = snapshot.content;
    this.changeReason = snapshot.reason;
    this.inheritanceMode = snapshot.mode;
    this.activeField = snapshot.field;
    this.markEditorChanged();
    if (!this.hasCurrentServerValidation)
      this.fieldErrors.set(this.validateEditor());
    this.historyMessage.set(message);
    this.restorePosition(snapshot);
  }

  private clearLocalHistory(): void {
    ++this.caretRequestId;
    this.pendingInput = null;
    this.composition = null;
    this.localHistory.clear();
    this.referenceSelection.set(null);
    this.serverValidationValue = null;
    this.comparisonDiffCache = null;
    this.historyMessage.set(null);
  }

  private resetLocalHistory(): void {
    this.clearLocalHistory();
    this.activeField = 'wording';
    this.localHistory.reset(this.editSnapshot('wording'));
  }

  private restorePosition(snapshot: TerminologyEditSnapshot): void {
    const request = ++this.caretRequestId,
      generation = this.operationGeneration,
      identity = this.contextIdentity();
    window.setTimeout(() => {
      if (
        this.destroyRef.destroyed ||
        request !== this.caretRequestId ||
        generation !== this.operationGeneration ||
        identity !== this.contextIdentity() ||
        this.workspaceView !== 'edit' ||
        !this.canEdit ||
        this.busy ||
        this.comparingCurrent() ||
        this.composition !== null
      )
        return;
      const input =
        snapshot.field === 'note'
          ? this.noteInput?.nativeElement
          : this.wordingInput?.nativeElement;
      if (!input?.isConnected || input.disabled) return;
      if (snapshot.field === 'note')
        input.closest('details')?.setAttribute('open', '');
      input.focus();
      input.setSelectionRange(
        snapshot.selectionStart ?? input.value.length,
        snapshot.selectionEnd ?? input.value.length,
      );
      input.scrollTop = snapshot.scrollTop;
      input.scrollLeft = snapshot.scrollLeft;
      if (snapshot.field === 'wording') this.captureReferenceSelection();
    });
  }

  refreshRevisionHistory(): void {
    const requestId = ++this.historyRequestId;
    this.isHistoryLoading.set(true);
    this.historyError.set(null);
    this.service
      .getVocabularyHistory(
        this.definition.definitionId,
        this.definition.configurationVersionId ?? undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (history) => {
          if (requestId !== this.historyRequestId) {
            return;
          }
          this.isHistoryLoading.set(false);
          this.revisionHistory.set(history);
        },
        error: (error) => {
          if (requestId !== this.historyRequestId) {
            return;
          }
          this.isHistoryLoading.set(false);
          this.historyError.set(mapTerminologyAdminError(error));
        },
      });
  }

  toDisplayLabel(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    return normalized
      ? normalized
          .split(/[._-]+/)
          .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' ')
      : 'Not set';
  }

  scopeLabel(item: TerminologyVocabularyCatalogItem): string {
    return [
      item.localeCode || 'all locales',
      item.channelCode || 'all channels',
      item.audienceCode || 'all audiences',
      item.tenantId ? `tenant ${item.tenantId}` : 'all tenants',
    ].join(' / ');
  }

  private safeRoute(
    locations: ReadonlyArray<{ kind: string; value: string }>,
  ): string | null {
    const route = locations.find(
      (location) =>
        location.kind.trim().toLowerCase() === 'route' &&
        location.value.startsWith('/') &&
        !location.value.startsWith('//') &&
        !location.value.includes(':'),
    );
    return route?.value ?? null;
  }

  private resetEditorFromDefinition(
    preserveSaveMessage: boolean,
    preserveHistory = false,
  ): void {
    this.historyRequestId += 1;
    this.revisionHistory.set([]);
    this.isHistoryLoading.set(false);
    this.historyError.set(null);
    this.editorError.set(null);
    if (!preserveSaveMessage) {
      this.saveMessage.set(null);
    }
    this.isSaving.set(false);
    this.isReloading.set(false);
    this.waitingForComparison = false;
    this.comparingCurrent.set(false);
    this.clearComparisonDecision();
    this.comparisonLoadFailed.set(false);
    this.fieldErrors.set([]);
    this.pendingSaveIntent = null;
    this.initializedRevision = this.revisionIdentity();
    this.draftContent = this.definition.currentText;
    this.changeReason = '';
    this.inheritanceMode =
      this.definition.currentReferenceTokenCodes.length > 0
        ? 'composed'
        : 'literal';
    this.captureBaseline();
    if (!preserveHistory) this.resetLocalHistory();
    this.publishEditingState();
  }

  private captureBaseline(): void {
    this.baselineContent = this.draftContent;
    this.baselineReason = this.changeReason;
    this.baselineInheritanceMode = this.inheritanceMode;
    this.captureReviewBaseline();
  }

  private captureReviewBaseline(): void {
    this.baselineReviewBehavior = this.comparisonCurrentBehavior;
    this.baselineReviewSource = this.comparisonCurrentSource;
  }

  private comparisonDecisionIdentity(): string {
    return JSON.stringify([
      this.contextIdentity(),
      this.revisionIdentity(),
      this.operationGeneration,
    ]);
  }

  private clearComparisonDecision(): void {
    this.confirmingUseCurrent.set(false);
    this.discardConfirmationIdentity = '';
  }

  private focusComparison(target: 'heading' | 'discard' | 'useCurrent'): void {
    const identity = this.comparisonDecisionIdentity(),
      request = ++this.caretRequestId;
    afterNextRender(
      () =>
        window.setTimeout(() => {
          if (
            this.destroyRef.destroyed ||
            request !== this.caretRequestId ||
            identity !== this.comparisonDecisionIdentity() ||
            !this.comparingCurrent() ||
            this.busy ||
            this.helpOpen
          )
            return;
          const element =
            target === 'heading'
              ? this.comparisonHeading?.nativeElement
              : target === 'discard'
                ? this.discardConfirmation?.nativeElement
                : this.useCurrentButton?.nativeElement;
          if (
            !element?.isConnected ||
            (target === 'discard' && !this.confirmingUseCurrent())
          )
            return;
          element.focus();
        }),
      { injector: this.injector },
    );
  }

  private focusWording(): void {
    const identity = this.contextIdentity();
    const generation = this.operationGeneration;
    const request = ++this.caretRequestId;
    afterNextRender(
      () =>
        window.setTimeout(() => {
          if (
            !this.destroyRef.destroyed &&
            request === this.caretRequestId &&
            generation === this.operationGeneration &&
            identity === this.contextIdentity() &&
            this.workspaceView === 'edit' &&
            this.wordingInput?.nativeElement.isConnected &&
            !this.wordingInput.nativeElement.disabled
          )
            this.wordingInput?.nativeElement.focus();
        }),
      { injector: this.injector },
    );
  }

  private adoptPublication(
    publication: TerminologyVocabularyPublicationDetail,
  ): void {
    const previous = this.definition;
    const compositionBaseRevisionId =
      publication.compositionBaseRevisionId ?? null;
    const definition: TerminologyVocabularyCatalogItem = {
      ...previous,
      currentText: publication.contentText,
      activeRevisionId: publication.revisionId,
      activePublicationId: publication.publicationId,
      initialWording: null,
      currentReferenceTokenCodes: parseTerminologyReferenceTokenCodes(
        publication.contentText,
      ),
      currentEffectiveStartUtc: publication.effectiveStartUtc,
      currentEffectiveEndUtc: publication.effectiveEndUtc ?? null,
      currentCompositionBaseRevisionId: compositionBaseRevisionId,
      currentCompositionBaseText:
        compositionBaseRevisionId === previous.activeRevisionId
          ? previous.currentText
          : compositionBaseRevisionId ===
              previous.currentCompositionBaseRevisionId
            ? previous.currentCompositionBaseText
            : null,
    };
    this.detail = {
      ...this.detail,
      definition,
      familyMembers: this.detail.familyMembers.map((member) =>
        member.definitionId === definition.definitionId ? definition : member,
      ),
    };
    this.initializedRevision = this.revisionIdentity();
  }

  private contextIdentity(): string {
    const value = this.definition;
    return JSON.stringify([
      this.editorContextKey,
      value.definitionId,
      value.configurationVersionId,
      value.configurationCode,
      value.tokenCode,
      value.localeCode,
      value.channelCode,
      value.audienceCode,
      value.tenantId,
    ]);
  }

  private revisionIdentity(): string {
    return JSON.stringify([
      this.definition.activeRevisionId,
      this.definition.activePublicationId,
      this.definition.currentText,
      this.definition.initialWording?.snapshotId ?? null,
    ]);
  }

  private publishEditingState(): void {
    this.editingStateChange.emit({
      dirty: this.hasUnsavedChanges,
      busy: this.busy,
    });
  }

  private createUpdateRequest(): TerminologyVocabularyWordingUpdateRequest {
    const request: TerminologyVocabularyWordingUpdateRequest = {
      configurationVersionId: this.definition.configurationVersionId!,
      expectedRevisionId: this.definition.activeRevisionId ?? null,
      expectedPublicationId: this.definition.activePublicationId ?? null,
      contentText: this.draftContent.trim(),
      changeReason: this.changeReason.trim() || null,
    };
    if (this.isInitialWording)
      request.initialWordingSnapshotId =
        this.definition.initialWording!.snapshotId;
    if (!this.supportsInheritanceChoice) {
      return request;
    }

    if (this.inheritanceMode === 'literal') {
      if (
        this.definition.currentCompositionBaseRevisionId == null &&
        this.definition.activeRevisionId != null
      ) {
        request.compositionBaseRevisionId = this.definition.activeRevisionId;
      }
    } else if (this.definition.currentCompositionBaseRevisionId != null) {
      request.clearCompositionBaseRevision = true;
    }
    return request;
  }

  private validationValue(): string {
    return JSON.stringify([
      this.draftContent.trim(),
      this.changeReason.trim(),
      this.inheritanceMode,
    ]);
  }

  private get hasCurrentServerValidation(): boolean {
    return (
      [400, 422].includes(this.editorError()?.statusCode ?? 0) &&
      this.serverValidationValue === this.validationValue()
    );
  }

  private get mustKeepRequiredReferences(): boolean {
    return (
      this.inheritanceMode === 'composed' ||
      this.isInitialWording ||
      (!this.supportsInheritanceChoice &&
        this.definition.currentCompositionBaseRevisionId == null)
    );
  }

  private validateEditor(): string[] {
    const messages: string[] = [];
    const content = this.draftContent.trim();
    const references = parseTerminologyReferenceTokenCodes(content);
    if (!content) {
      messages.push('Wording is required.');
    }
    if (
      this.minimumLength !== null &&
      [...content].length < this.minimumLength
    ) {
      messages.push(
        `Wording must contain at least ${this.minimumLength} characters.`,
      );
    }
    if ([...content].length > this.maximumLength) {
      messages.push(`Wording cannot exceed ${this.maximumLength} characters.`);
    }
    if (!this.detail.constraints.isValid) {
      messages.push(
        'This definition has invalid constraint metadata and cannot be safely edited.',
      );
    }
    if (!this.detail.constraints.allowReferences && references.length > 0) {
      messages.push('References are not allowed for this definition.');
    }

    const missingRequired =
      this.detail.constraints.requiredReferenceTokenCodes.filter(
        (required) => !references.includes(required.toLowerCase()),
      );
    if (this.mustKeepRequiredReferences && missingRequired.length > 0) {
      messages.push(
        `Required references are missing: ${missingRequired.join(', ')}.`,
      );
    }
    const allowed = this.detail.constraints.allowedReferenceTokenCodes;
    const disallowed =
      allowed == null
        ? []
        : references.filter(
            (reference) =>
              !allowed.some(
                (candidate) => candidate.toLowerCase() === reference,
              ),
          );
    if (disallowed.length > 0) {
      messages.push(
        `These references are not allowed: ${disallowed.join(', ')}.`,
      );
    }
    const unknown = this.resolvedDraft.references.filter(
      (reference) => !reference.isKnown,
    );
    if (unknown.length > 0) {
      messages.push(
        `These references are not available in the active scope: ${unknown.map((item) => item.tokenCode).join(', ')}.`,
      );
    }
    if (this.inheritanceMode === 'literal' && references.length > 0) {
      messages.push('Fixed wording cannot contain terminology references.');
    }
    return messages;
  }
}
